/* 수주관리 통계 대시보드 — /contract 상단 영역 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useContractAPI } from '../../api/contract/useContractAPI';
import { buildContractStats } from './contractStats';

import DashboardStatCard from '../../components/Dashboard/DashboardStatCard';
import DonutChart from '../../components/Chart/DonutChart';
import HorizontalBarChart from '../../components/Chart/HorizontalBarChart';
import LineChartCard from '../../components/Chart/LineChartCard';
import DashboardPanel from '../../components/Layout/DashboardPanel';
import DateRangePicker from '../../components/DateRangePicker/DateRangePicker';

const SIZE_CAP = 5000;

const formatYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const default30DayRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return { start: formatYmd(start), end: formatYmd(end) };
};

const normalizeListResponse = (res) => {
  if (!res) return { list: [], totalCount: null };
  if (Array.isArray(res)) return { list: res, totalCount: res.length };
  if (Array.isArray(res.contracts)) {
    return {
      list: res.contracts,
      totalCount: res?.page?.totalCount ?? res.contracts.length,
    };
  }
  if (Array.isArray(res.list)) {
    return {
      list: res.list,
      totalCount: res?.page?.totalCount ?? res.list.length,
    };
  }
  return { list: [], totalCount: null };
};

const ContractStatDashboard = ({ refreshSignal = 0, lastContractDt = null }) => {
  const session = useSelector((state) => state.session.data);
  const api = useContractAPI();
  const [list, setList] = useState([]);
  const [range, setRange] = useState(default30DayRange);
  const [capExceeded, setCapExceeded] = useState(false);

  // 등록된 수주의 일자가 현재 range 시작보다 과거면 range 를 확장해 카드에 즉시 반영.
  useEffect(() => {
    if (!lastContractDt) return;
    setRange((prev) => {
      if (!prev.start || lastContractDt < prev.start) {
        return { ...prev, start: lastContractDt };
      }
      return prev;
    });
  }, [lastContractDt]);

  useEffect(() => {
    const companySeq = session?.companySeq;
    if (!companySeq) return;
    if (!range.start || !range.end) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.contract.get({
          companySeq,
          dateType: 'contractDt',
          contractStartDt: range.start,
          contractEndDt: range.end,
          page: 0,
          size: SIZE_CAP,
        });
        if (cancelled) return;
        const { list: normalized, totalCount } = normalizeListResponse(res);
        if (typeof totalCount === 'number' && totalCount > SIZE_CAP) {
          // eslint-disable-next-line no-console
          console.warn(
            `[ContractStatDashboard] totalCount=${totalCount} > size cap ${SIZE_CAP} — 일부만 집계됨`
          );
          setCapExceeded(true);
        } else {
          setCapExceeded(false);
        }
        setList(normalized);
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('[ContractStatDashboard] fetch failed', err);
          setList([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.companySeq, range.start, range.end, refreshSignal]);

  const stats = useMemo(() => buildContractStats(list), [list]);

  const dailySeries = useMemo(
    () =>
      (stats.daily || []).map((d) => ({
        name: d.date,
        value: d.count,
      })),
    [stats.daily]
  );

  return (
    <DashboardPanel
      controls={
        <div className="contract-stat-range">
          <DateRangePicker
            startKey="start"
            endKey="end"
            formData={range}
            setFormData={setRange}
            split
          />
          {capExceeded ? (
            <span
              data-testid="contract-stat-cap-warning"
              className="badge bg-warning text-dark"
              style={{ marginLeft: 12 }}
            >
              일부만 집계됨 (size={SIZE_CAP} 초과)
            </span>
          ) : null}
        </div>
      }
    >
      <div className="contract-stat-cards">
        <DashboardStatCard
          title="기간 수주 건수"
          value={stats.totalCount}
          unit="건"
          subtitle={`${range.start} ~ ${range.end}`}
        />
        <DashboardStatCard
          title="납기 준수율"
          value={stats.ontimeRate == null ? '–' : stats.ontimeRate}
          unit={stats.ontimeRate == null ? '' : '%'}
          subtitle={
            stats.ontimeRate == null
              ? '출고 데이터 없음'
              : `정시 ${
                  stats.statusDistribution.find((d) => d.label === '정시출고')
                    ?.value || 0
                } / 출고 ${
                  (stats.statusDistribution.find((d) => d.label === '정시출고')
                    ?.value || 0) +
                  (stats.statusDistribution.find((d) => d.label === '지연출고')
                    ?.value || 0)
                }`
          }
        />
      </div>

      <div className="contract-stat-charts">
        <LineChartCard
          data={dailySeries}
          xKey="name"
          yKey="value"
          yLabel="수주 건수"
          title="일별 수주"
          width="100%"
          height={240}
        />
        <DonutChart
          data={stats.statusDistribution}
          title="상태별 분포"
          topN={5}
        />
        <HorizontalBarChart
          data={stats.partnerTop5}
          title="거래처 탑5"
          topN={5}
        />
      </div>
    </DashboardPanel>
  );
};

export default ContractStatDashboard;
